// The failure vocabulary of the launch's first leg (`/lti/login`, TECH-3181).
//
// Shared rather than server-private because both ends of one redirect need it:
// the server puts a code in `?code=` and labels `elaso_lti_login_total` with it,
// and the client's `/lti/error` route (TECH-3186) maps it to copy. One list means
// a code cannot be renamed on one side only.
//
// The codes are deliberately finer-grained than the copy they produce. Whoever
// sees the page is usually a student who can do nothing about a half-finished
// registration, so several codes share one generic "ask your administrator"
// message. The log line and the dashboard keep them apart, because the
// administrator being asked needs to know which one it was.
export const LTI_LOGIN_ERROR_CODES = [
	// The initiation itself was unusable, with a required param missing or blank,
	// or a `target_link_uri` that does not point at this tool.
	"invalid_request",
	// Nothing matches `(iss, client_id)`, or `client_id` was omitted and the
	// issuer alone matches more than one registration. Either this LTI Platform
	// was never onboarded, or the client id in its Developer Key is not the one we
	// hold.
	"registration_not_found",
	// The registration exists but cannot serve a launch. Its config is empty (the
	// state TECH-3180's creation flow leaves behind), unparseable, or carries an
	// empty deployment allowlist, which means "no launch is accepted yet"
	// (TECH-3177).
	"registration_incomplete",
	// The initiation named a `lti_deployment_id` that is not on the allowlist.
	"deployment_not_allowed",
	// The tenant resolved, and is switched off.
	"organization_disabled",
	// Over the per-IP budget for `/lti/login`.
	"rate_limited",
	// The launch-state store could not answer. The launch is refused here rather
	// than in leg 3, after the user has authenticated (TECH-3179 fails closed).
	// The only code in this list a user can act on by retrying.
	"launch_unavailable",
	// An unexpected throw. Nothing about it reaches the URL.
	"internal",
] as const;

export type LtiLoginErrorCode = (typeof LTI_LOGIN_ERROR_CODES)[number];

/** What `/lti/login` did, as counted on `elaso_lti_login_total`. */
export type LtiLoginOutcome = "ok" | LtiLoginErrorCode;

// The failure vocabulary of the launch's third leg (`/lti/launch`, TECH-3182).
// The LTI Platform-signed `id_token` arrives there and either passes every check
// or produces exactly one of these. Same contract as the login list above, a
// code on the redirect, a label on `elaso_lti_launch_total`, and copy on the
// client's `/lti/error` route.
//
// Kept a separate list rather than folded into the login one, even though six
// names repeat, because the leg that emitted a code is the first thing anyone
// debugging a launch wants to know. `registration_incomplete` from leg 1 is an
// unfinished onboarding, while the same code from leg 3 means the registration
// changed underneath a launch already in flight.
export const LTI_LAUNCH_ERROR_CODES = [
	// The form POST was not a launch, with `state` or `id_token` missing or blank.
	"invalid_request",
	// No launch-state record for this `state`, so it is unknown, expired (10 min)
	// or already used. The ordinary cause is a refresh or a back-button replay of
	// the launch POST, and it is the only code here a user can act on, by clicking
	// the link in the course again.
	"launch_expired",
	// Not a well-formed RS256 JWS: wrong segment count, header or payload that is
	// not JSON, an `alg` we refuse (`none`, HS*), or an unrecognised `crit`.
	"token_malformed",
	// The LTI Platform's key set has no key matching the token's `kid`, or the
	// signature does not verify under the key it names. A forged token lands here.
	"signature_invalid",
	// `exp` has passed, or `iat` is outside the launch window, either in the
	// future or far enough in the past that no live launch could have produced it.
	"token_expired",
	// The signature is good and the token is still the wrong one for this launch.
	// `iss`, `aud` or `azp` names another registration, the `nonce` is not the one
	// this `state` was issued with, or a `target_link_uri` is off our origin.
	"token_rejected",
	// The signed `deployment_id` claim is not on this registration's allowlist,
	// the authoritative version of leg 1's unsigned pre-check.
	"deployment_not_allowed",
	// A `message_type` we do not implement (deep linking) or a `version` that is
	// not `1.3.0`.
	"unsupported_message",
	// The claims are fine and the registration is not. It is gone, unusable or
	// switched off between leg 1 and leg 3. Rare by construction, because a launch
	// is a ten-minute window, and always an admin action mid-flight.
	"registration_not_found",
	"registration_incomplete",
	"organization_disabled",
	// Over the per-IP budget for `/lti/launch`. Its own budget, not leg 1's.
	"rate_limited",
	// The launch-state store or the platform's JWKS endpoint could not answer.
	// Retryable, and the only code here that is about us rather than the token.
	"launch_unavailable",
	// An unexpected throw. Nothing about it reaches the URL.
	"internal",

	// Resolution (TECH-3184). Everything above is about the token. Everything
	// below is about what the token turned out to name. The launch is genuine, and
	// ELASO cannot seat the user behind it. Five of the six are the M3 boundary,
	// where the launch REFUSES today and a later issue replaces the behaviour
	// behind the code, at which point some stop being reachable rather than being
	// renamed.

	// The `context` claim is absent, or its `context.id` matches no
	// Organization-Workspace link in this Organization. TECH-3188 replaces this
	// with the not-connected page plus a recorded pending Context.
	"workspace_not_linked",
	// No User in the Organization matches the `sub` (through `externalRef`) or, on
	// a first launch, the `email` claim. UNREACHABLE since TECH-3190, which
	// provisions an eligible launcher instead. Kept rather than deleted, because a
	// code is a contract with the client page and with anyone reading an old
	// dashboard.
	"user_not_found",
	// The User exists and is tombstoned (`deletedAt`), so login is revoked while
	// authored history is preserved. Deliberately NOT folded into
	// `user_not_found`, because an admin needs "I deactivated them" to look
	// different from "they were never here", and TECH-3190 must keep this
	// distinction or JIT quietly becomes an un-revocation path.
	"user_deactivated",
	// The User exists with no Firebase Identity attached (`firebaseUid` null), the
	// invited or pre-attach state. There is nothing to mint a custom token for.
	// UNREACHABLE since TECH-3190, which backfills the Identity instead.
	"identity_missing",
	// The User exists, has an Identity, and holds no Participation in the mapped
	// Workspace, so they are a user of the Organization who is not in this course.
	// UNREACHABLE since TECH-3190, which grants Participation instead.
	"not_a_participant",
	// Provisioning (TECH-3190). The three states JIT provisioning cannot serve.
	// It now fixes everything else it used to refuse (`user_not_found`,
	// `identity_missing`, `not_a_participant`), so those three are unreachable and
	// kept only as vocabulary.

	// None of the launcher's LTI roles maps to an ELASO role (TECH-3194), so they
	// are an observer, a parent, a course designer or an unenrolled admin. They do
	// not belong in the collaboration space, and provisioning them would seed the
	// Workspace with users who do not belong in it. The only code here aimed at
	// the launcher rather than at an administrator.
	"role_not_recognized",
	// The launch carries no `email` claim, or no usable name, because the
	// registration's privacy level is not "public". `User.email` is required and
	// unique per Organization, and a nameless User renders as the neutral
	// placeholder, so there is nothing to create. Synthesizing either is forbidden
	// outright (TECH-3176 §4). The LMS has to be told to share them.
	"claims_insufficient",
	// The Workspace is linked to this Context but its canonical Roles were never
	// seeded, so the role the launcher maps to does not exist to grant. Distinct
	// from `workspace_not_linked` (nobody connected the course) and from
	// `registration_incomplete` (leg 1's config). Re-running the link from
	// `/platform` clears this one, and it should never be seen, because every path
	// that creates a link runs the seed.
	"workspace_not_ready",
	// The launch names one user two irreconcilable ways. The drifted `email` claim
	// is held by another User in the Organization, or by another Firebase
	// Identity, or the `sub` is already bound to a different User. One code for
	// all three because they mean the same thing to whoever reads the page. The
	// log line and the Sentry event name which one it was, and both sides of it.
	// Nothing is written. See `lib/lti/resolution.ts`.
	//
	// This is the one code here that can lock out a legitimate user. An upstream
	// rename into an address ELASO already knows needs a human to resolve. That is
	// the accepted price of never signing anyone in on an ambiguous identity.
	"identity_conflict",
] as const;

export type LtiLaunchErrorCode = (typeof LTI_LAUNCH_ERROR_CODES)[number];

/** What `/lti/launch` did, as counted on `elaso_lti_launch_total`. */
export type LtiLaunchOutcome = "ok" | LtiLaunchErrorCode;

/**
 * Anything `/lti/error?code=` can carry, from either leg. The page (TECH-3186)
 * maps this union to copy. The two legs keep their own lists so a code always
 * says which one produced it.
 */
export type LtiErrorCode = LtiLoginErrorCode | LtiLaunchErrorCode;

// The failure vocabulary of `POST /lti/exchange` (TECH-3184), the one-time
// handoff that turns a verified launch into a Firebase custom token.
//
// A THIRD list, and a different contract from the two above. The caller here is
// our own client (the TECH-3186 landing route) rather than a browser
// mid-redirect, so the exchange answers JSON with a code in the body and never
// redirects. Kept separate for the same reason legs 1 and 3 are separate. The
// step that produced a failure is the first thing anyone debugging wants.
//
// There is no `user_not_found`-shaped code here. Every identity question was
// settled during the launch, so by the time a code exists the only things that
// can go wrong are the code itself and the mint.
export const LTI_EXCHANGE_ERROR_CODES = [
	// The body carried no usable `code`, or one that is not the shape we mint.
	"invalid_request",
	// Unknown, expired (60s), or already redeemed. Indistinguishable by design,
	// and single-use means a second attempt with a genuine code lands here too,
	// which is what a double-mounted landing route looks like.
	"code_invalid",
	// Over the per-IP budget for `/lti/exchange`. Its own budget, not a launch's.
	"rate_limited",
	// The handoff store could not answer. Fails CLOSED, like the launch state it
	// sits beside, because a handoff we cannot verify is one we refuse. Retryable
	// only in the sense that the whole launch must be retried. The code is gone.
	"exchange_unavailable",
	// Firebase refused to mint the custom token, or an unexpected throw. Nothing
	// about it reaches the client beyond this code.
	"internal",
] as const;

export type LtiExchangeErrorCode = (typeof LTI_EXCHANGE_ERROR_CODES)[number];

/** What `/lti/exchange` did, as counted on `elaso_lti_exchange_total`. */
export type LtiExchangeOutcome = "ok" | LtiExchangeErrorCode;

/**
 * The exchange's success body. `workspaceId` is the Workspace the launch
 * resolved, and the client sets it as current rather than guessing a default
 * (TECH-3186 owns what it does with it).
 */
export type LtiExchangeResponse = {
	customToken: string;
	workspaceId: string;
};
